import { useEffect, useMemo, useState } from 'react'
import { AxiosError } from 'axios'

import { useQuery } from '@tanstack/react-query'

import { useDispatch } from '@tabeeb/store'
import { useDebouncedValue } from '@collaborate-health/uikit'
import { onAddErrorNotification } from '@tabeeb/modules/notification/actions'
import { ApiErrorResponse } from '@tabeeb/contracts/main-api/swagger-main-api'
import { useApi } from '@tabeeb/modules/shared/utils/API/hooks'
import getErrorMessageFromResponse from '@tabeeb/modules/shared/utils/getErrorMessageFromResponse'

import { CertificatesQueryKeys } from '../queries'

export interface UseAllCertificateTypesProps {
  enabled?: boolean
  showCurrentUserCertificates?: boolean
}

const useAllCertificateTypes = (
  { enabled, showCurrentUserCertificates }: UseAllCertificateTypesProps = {
    showCurrentUserCertificates: false,
    enabled: true,
  }
) => {
  const dispatch = useDispatch()
  const { certificateServiceApi: api } = useApi()

  const [search, setSearch] = useState('')
  const debouncedSearch = useDebouncedValue(search, 500)

  const certificateTypesQuery = useQuery({
    queryKey: [CertificatesQueryKeys.CertificateTypesAll, showCurrentUserCertificates, debouncedSearch],
    queryFn: () =>
      api.certificateTypes.getAllAsync({
        ShowCurrentUserCertificates: showCurrentUserCertificates,
        Search: debouncedSearch || undefined,
      }),
    refetchOnMount: false,
    enabled,
  })

  const { isError: isTypesError, error: typesError } = certificateTypesQuery

  useEffect(() => {
    if (isTypesError && typesError) {
      const error = typesError as AxiosError<ApiErrorResponse>
      const message = getErrorMessageFromResponse(error.response?.data)
      dispatch(onAddErrorNotification({ message }))
    }
  }, [isTypesError, typesError, dispatch])

  return useMemo(
    () => ({
      loading: certificateTypesQuery.isFetching,
      certificateTypes: certificateTypesQuery.data ?? [],
      onReload: certificateTypesQuery.refetch,
      setCertificateTypesSearch: setSearch,
      certificateTypesSearch: search,
    }),
    [certificateTypesQuery.data, certificateTypesQuery.isFetching, certificateTypesQuery.refetch, search]
  )
}

export default useAllCertificateTypes

